import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    LinearProgress,
    Box,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from 'modules/auth/contexts/AuthContext';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;   // 10 minutes
const WARNING_BEFORE_MS = 60 * 1000;       // show warning 1 minute before logout
const WARNING_AT_MS = IDLE_TIMEOUT_MS - WARNING_BEFORE_MS; // 9 minutes

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

const SessionTimeoutManager = () => {
    const { isAuthenticated, logout } = useAuth();
    const [warningOpen, setWarningOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(60);

    const idleTimer = useRef(null);
    const warningTimer = useRef(null);
    const countdownInterval = useRef(null);

    const clearAllTimers = useCallback(() => {
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);
        clearInterval(countdownInterval.current);
    }, []);

    const startLogoutCountdown = useCallback(() => {
        setSecondsLeft(60);
        countdownInterval.current = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    clearInterval(countdownInterval.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        idleTimer.current = setTimeout(() => {
            setWarningOpen(false);
            localStorage.setItem('logout_reason', 'Your session expired due to inactivity. Please log in again.');
            logout();
        }, WARNING_BEFORE_MS);
    }, [logout]);

    const resetTimers = useCallback(() => {
        if (!isAuthenticated) return;
        clearAllTimers();
        setWarningOpen(false);

        warningTimer.current = setTimeout(() => {
            setWarningOpen(true);
            startLogoutCountdown();
        }, WARNING_AT_MS);
    }, [isAuthenticated, clearAllTimers, startLogoutCountdown]);

    const handleStayLoggedIn = useCallback(() => {
        setWarningOpen(false);
        resetTimers();
    }, [resetTimers]);

    useEffect(() => {
        if (!isAuthenticated) {
            clearAllTimers();
            setWarningOpen(false);
            return;
        }

        resetTimers();

        const onActivity = () => resetTimers();
        ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

        return () => {
            clearAllTimers();
            ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
        };
    }, [isAuthenticated, resetTimers, clearAllTimers]);

    if (!isAuthenticated) return null;

    const progress = (secondsLeft / 60) * 100;

    return (
        <Dialog
            open={warningOpen}
            disableEscapeKeyDown
            PaperProps={{ sx: { minWidth: 340, borderRadius: 2 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon color="warning" />
                Session Expiring Soon
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    You have been inactive. Your session will expire in:
                </Typography>
                <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Typography variant="h3" color={secondsLeft <= 15 ? 'error.main' : 'warning.main'} fontWeight={700}>
                        {secondsLeft}s
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={secondsLeft <= 15 ? 'error' : 'warning'}
                    sx={{ height: 6, borderRadius: 3 }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button variant="outlined" color="error" onClick={logout} size="small">
                    Logout Now
                </Button>
                <Button variant="contained" color="primary" onClick={handleStayLoggedIn} autoFocus>
                    Stay Logged In
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionTimeoutManager;
